import { useRef, useState } from "react";
import dayjs from "dayjs";
import { useRouter } from "next/router";

import { Routes } from "../../generated/routes";
import { useDevice } from "../../contexts/deviceContext";
import { PrefectureItem } from "../../types";
import { useForm } from "../../form";
import { nub } from "../../lib/fun";
import { useCurrentEntor, useGQLClient } from "../../contexts/appContext";
import { EventDetailPageProps } from "./PageEventDetail";
import { CancelEventForm } from "../../components/molecules/cancelEventRegistration";
import {
  entryEventRegistrationStatus,
  EventRegistrationStatus,
  getJobhuntInfoEditPath,
  scheduledEventDateRegistrationStatus,
} from "../../common";
import { useEffectSkipFirstRender } from "../../hooks/useEffectSkipFirstRender";

export type EventDetailForm = {
  selectedPrefecture: PrefectureItem | null;
};

function isElementInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight ||
        document.documentElement.clientHeight) /* or $(window).height() */ &&
    rect.right <=
      (window.innerWidth ||
        document.documentElement.clientWidth) /* or $(window).width() */
  );
}

export const usePageEventDetail = ({ event, venue }: EventDetailPageProps) => {
  const headlines = event.contents
    .map((content) => content.contentInstance)
    .map((content) =>
      content.__typename === "ContentHeadline" ? content : undefined,
    )
    .filter((hc) => Boolean(hc))
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    .map((hc) => hc!);

  const headlineRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const setHeadlineRef = (id: number, ref: HTMLDivElement | null) => {
    headlineRefs.current[id] = ref;
  };

  const device = useDevice();

  const router = useRouter();

  const { gqlClient } = useGQLClient();

  const goToHeadline = (id: number) => {
    headlineRefs.current[id]?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const [activeHeadline, setActiveHeadline] = useState(-1);

  const registeredDate = event.scheduledEvent
    ? event.scheduledEvent.dates.find((date) => date.currentEntorApplied)
    : null;

  // how to sort date with dayjs
  // https://runkit.com/embed/mpmqk4tnk79y
  const upcomingDates = event.scheduledEvent
    ? event.scheduledEvent.dates
        .filter(
          (date) =>
            // the date an entor applied for should be cancellable event after an event becomes outdated
            // so in case an entor applied for an old date and missed attending on that date, he/she can apply for another date the second time.
            dayjs().isBefore(dayjs(date.deadline)) || date.currentEntorApplied,
        )
        .sort((a, b) => (dayjs(a.startAt).isAfter(dayjs(b.startAt)) ? 1 : -1))
    : [];

  // when upcomingDates is an empty array, it's an entry type event
  const entryEventFull =
    event.entryEvent?.limitNum === 0 ||
    (event.entryEvent?.limitNum &&
      event?.entryEvent?.numApplied &&
      event?.entryEvent?.numApplied - event?.entryEvent?.limitNum >= 0)
      ? true
      : false;

  const scheduledEventFull = upcomingDates.every(
    (venue) => venue.numApplied - venue.reservationNum >= 0,
  );

  const eventIsFull =
    upcomingDates.length > 0 ? scheduledEventFull : entryEventFull;

  const initialSelectedDate = venue
    ? upcomingDates.find((date) => date.id === venue)
    : registeredDate
    ? registeredDate
    : upcomingDates.length === 1
    ? upcomingDates[0]
    : null;

  const upcomingPrefectures = nub(
    upcomingDates.map((date) => date.place.prefecture),
    (p) => p.id,
  );

  const upcomingPrefecturesOnlineFirst = [
    ...upcomingPrefectures.filter((p) => p.name === "オンライン開催"),
    ...upcomingPrefectures.filter((p) => p.name !== "オンライン開催"),
  ];

  const currentEntor = useCurrentEntor();

  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);

  const initialSelectedPrefecture =
    upcomingPrefecturesOnlineFirst.find(
      (p) =>
        initialSelectedDate && initialSelectedDate.place.prefecture.id === p.id,
    ) ||
    (upcomingPrefecturesOnlineFirst.length
      ? upcomingPrefecturesOnlineFirst[0]
      : null);

  const goToJobhuntInfoEdit = () => {
    const path = getJobhuntInfoEditPath();
    if (typeof path === "string") {
      window.location.replace(path);
    } else {
      router.push(path);
    }
  };

  const eventDetailForm = useForm<EventDetailForm>({
    initValue: {
      selectedPrefecture: initialSelectedPrefecture,
    },
    validators: {},
  });

  useEffectSkipFirstRender(() => {
    setSelectedDate(null);
  }, [eventDetailForm.formValues.selectedPrefecture]);

  const computeEventRegistrationStatus = ():
    | EventRegistrationStatus
    | "dateNotSelected" => {
    if (event.entryEvent) {
      return entryEventRegistrationStatus(
        event,
        event.entryEvent,
        currentEntor,
      );
    } else {
      if (selectedDate) {
        return scheduledEventDateRegistrationStatus(
          event,
          selectedDate,
          currentEntor,
        );
      } else {
        return "dateNotSelected";
      }
    }
  };

  const onScroll = () => {
    const newActiveHeadline =
      headlines.find((hl) => {
        const el = headlineRefs.current[hl.id];
        return el && isElementInViewport(el);
      })?.id || -1;
    setActiveHeadline(newActiveHeadline);
  };

  const registrationURL = event.entryEvent
    ? Routes.url("/events/[hash]/register", { hash: event.hash })
    : selectedDate
    ? Routes.url("/events/[hash]/register", {
        hash: event.hash,
        venue: selectedDate.id,
      })
    : undefined;

  const cancelEvent = async (form: CancelEventForm) => {
    if (event.entryEvent) {
      await gqlClient.CancelEntryEvent({
        entryEventId: event.entryEvent.id,
        ...form,
      });
    } else if (selectedDate) {
      await gqlClient.CancelScheduledEventDate({
        scheduledEventDateId: selectedDate.id,
        ...form,
      });
    }

    // TODO: reload but keep current selected date?
    await router.reload();
  };

  return {
    event,
    eventIsFull,
    headlines,
    device,
    headlineRefs,
    goToHeadline,
    setHeadlineRef,
    activeHeadline,
    onScroll,
    hasRegisteredVenue: Boolean(registeredDate), // TODO
    upcomingDates,
    upcomingPrefectures: upcomingPrefecturesOnlineFirst,
    eventRegistrationStatus: computeEventRegistrationStatus(),
    eventDetailForm,
    selectedDate,
    setSelectedDate,
    registrationURL,
    initialSelectedPrefecture,
    cancelEvent,
    goToJobhuntInfoEdit,
  };
};
